import mapboxgl from "mapbox-gl";
import * as turf from "@turf/turf";

import { TI_LAYER_IDS } from "../../component/constants/transmissionInsightsConstants";
import { isValidLngLat } from "../map-utils";
import { getPaintProperty } from "./filter-expression-utils";

import TransmissionLineTooltip from "../../component/map/TransmissionLineTooltip";
import SubstationTooltip from "../../component/map/SubstationTooltip";

export function handleBusClick(
    e: mapboxgl.MapMouseEvent,
    map: mapboxgl.Map,
    setActiveTooltip: (activeTooltip: any) => void
) {
    const clickedFeatures = map.queryRenderedFeatures(e.point, {
        layers: [TI_LAYER_IDS.BUS_LOCATION_LAYER_ID],
    });

    if (clickedFeatures.length > 0) {
        const currentPoint = clickedFeatures[0].properties?.BusNumber;
        const geometry = clickedFeatures[0].geometry as any;
        const currentPointCoords = geometry.coordinates;

        map.setLayoutProperty(TI_LAYER_IDS.BUS_LOCATION_LAYER_ID, "icon-size", [
            "interpolate",
            ["linear"],
            ["zoom"],
            0,
            ["case", ["==", ["get", "BusNumber"], currentPoint], 0.75, 0.5],
            5,
            ["case", ["==", ["get", "BusNumber"], currentPoint], 1, 0.55],
            10,
            ["case", ["==", ["get", "BusNumber"], currentPoint], 1.5, 0.7],
            22,
            ["case", ["==", ["get", "BusNumber"], currentPoint], 2, 1],
        ]);

        map.flyTo({
            center: currentPointCoords,
            zoom: 10,
            speed: 1.2,
            curve: 1.5,
        });
    } else {
        map.setPaintProperty(
            TI_LAYER_IDS.BUS_LOCATION_LAYER_ID,
            "circle-radius",
            [
                "interpolate",
                ["linear"],
                ["zoom"],
                0,
                3,
                5,
                3.3000000000000003,
                10,
                4.199999999999999,
                22,
                6,
            ]
        );
    }
}
export function handleLineClick(
    e: mapboxgl.MapMouseEvent,
    map: mapboxgl.Map,
    setActiveTooltip: (activeTooltip: any) => void
) {
    const clickedLine = map.queryRenderedFeatures(e.point, {
        layers: [TI_LAYER_IDS.TRANSMISSION_LINES_LAYER_ID],
    });

    if (clickedLine.length > 0) {
        const currentLineId = clickedLine[0].properties?.ID;
        const geometry = clickedLine[0].geometry as any;
        const lineCoords = geometry.coordinates;

        map.setPaintProperty(
            TI_LAYER_IDS.TRANSMISSION_LINES_LAYER_ID,
            "line-width",
            ["case", ["==", ["get", "ID"], currentLineId], 6, 2]
        );

        map.setPaintProperty(
            TI_LAYER_IDS.TRANSMISSION_LINES_LAYER_ID,
            "line-color",
            [
                "case",
                ["==", ["get", "ID"], currentLineId],
                "#cc548f",
                getPaintProperty("transmission-lines")!,
            ]
        );

        const lineBounds = lineCoords.reduce(
            (bounds: { extend: (arg0: any) => any }, coord: any) => {
                return bounds.extend(coord);
            },
            new mapboxgl.LngLatBounds(lineCoords[0], lineCoords[0])
        );

        map.fitBounds(lineBounds, {
            padding: 40,
            duration: 1200,
        });
    } else {
        map.setPaintProperty(
            TI_LAYER_IDS.TRANSMISSION_LINES_LAYER_ID,
            "line-width",
            2
        );
        map.setPaintProperty(
            TI_LAYER_IDS.TRANSMISSION_LINES_LAYER_ID,
            "line-color",
            "#ff69b4"
        );
    }
}
export function handleBusMouseEnter(
    e: mapboxgl.MapMouseEvent,
    map: mapboxgl.Map,
    setActiveTooltip: (activeTooltip: any) => void
) {
    map.getCanvas().style.cursor = "pointer";
    if (e.features && e.features[0]) {
        const geometry = e.features[0].geometry as any;
        const coordinates = geometry.coordinates.slice();
        const properties = e.features[0].properties;
        const projected = map.project(coordinates);
        let x = projected.x;
        let y = projected.y;

        if (
            map.getLayer(TI_LAYER_IDS.THERMAL_CAPACITY_NO_CLUSTER_LAYER_ID) &&
            map.getLayer(TI_LAYER_IDS.THERMAL_CAPACITY_WORST_CLUSTER_LAYER_ID)
        ) {
            const thermalFeatures = map.queryRenderedFeatures({
                layers: [
                    TI_LAYER_IDS.THERMAL_CAPACITY_NO_CLUSTER_LAYER_ID,
                    TI_LAYER_IDS.THERMAL_CAPACITY_WORST_CLUSTER_LAYER_ID,
                ],
            });

            let minDist = Infinity;
            let closestThermal = null;
            for (const feature of thermalFeatures) {
                if (feature.geometry.type === "Point") {
                    const coords = (feature.geometry as GeoJSON.Point)
                        .coordinates;
                    const dist = turf.distance(
                        turf.point(coordinates),
                        turf.point(coords),
                        { units: "kilometers" }
                    );
                    if (dist < minDist) {
                        minDist = dist;
                        closestThermal = feature;
                    }
                }
            }

            let cluster: string | undefined = undefined;
            let single: string | undefined = undefined;
            if (closestThermal && minDist <= 0.01) {
                cluster = `${closestThermal.properties?.WorstClusterTrLim}`;
                single = `${closestThermal.properties?.NoClusterTrLim}`;
            }

            setActiveTooltip(() => (
                <div
                    style={{
                        position: "absolute",
                        left: x,
                        top: y,
                        transform: "translate(-20%, -100%)",
                        zIndex: 1000,
                    }}
                >
                    {SubstationTooltip({
                        name: `${properties?.SubstationName}`,
                        voltage: `${properties?.Voltage || ""}kV`,
                        cluster: cluster || "N/A",
                        single: single || "N/A",
                        isOpen: true,
                    })}
                </div>
            ));
        }
    }
}
export function handleLineMouseEnter(
    e: mapboxgl.MapMouseEvent,
    map: mapboxgl.Map,
    setActiveTooltip: (activeTooltip: any) => void
) {
    map.getCanvas().style.cursor = "pointer";
    if (e.features && e.features[0]) {
        const geometry = e.features[0].geometry as any;
        let coordinates = Array.isArray(geometry.coordinates)
            ? geometry.coordinates.slice().pop()
            : undefined;

        // Use feature coordinates if valid, otherwise use mouse location
        let popupLngLat: mapboxgl.LngLatLike;
        if (isValidLngLat(coordinates)) {
            popupLngLat = coordinates as [number, number];
        } else if (isValidLngLat([e.lngLat.lng, e.lngLat.lat])) {
            popupLngLat = [e.lngLat.lng, e.lngLat.lat];
        } else {
            console.warn(
                "No valid coordinates for popup:",
                coordinates,
                e.lngLat
            );
            return;
        }

        const properties = e.features[0].properties;
        const projected = map.project(popupLngLat);
        let x = projected.x;
        let y = projected.y;

        setActiveTooltip(() => (
            <div
                style={{
                    position: "absolute",
                    left: x,
                    top: y,
                    transform: "translate(-20%, -100%)",
                    zIndex: 1000,
                }}
            >
                {TransmissionLineTooltip({
                    name: `${properties?.OWNER}`,
                    voltage: `${properties?.VOLTAGE || ""}kV`,
                    id: `${properties?.ID}`,
                    isOpen: true,
                })}
            </div>
        ));
    }
}
